import type { DatasetStructureChannel } from "@/api";
import { CHART_SERIES_PALETTE } from "@/shared/ui/chart/public";
import {
  GROUP_ORDER,
  channelTitle,
  renderGroup,
  type ParquetChannelGroupId,
} from "../parquetChannels";
import { DIRECTION_TOKEN, displayArmSide, type ArmSide } from "./motionArms";

/**
 * 운동 보기의 카드 모델. **채널 하나가 카드 하나다.**
 *
 * 진단 채널 키는 `<topic>::<name>` 이다. 같은 `name` 이 `action::ee_pose_left` 와
 * `observation_vector::ee_pose_left` 로 두 번 나오면, 두 키는 같은 대상의 명령 값과 측정 값이다
 * (`diagnosticsTerms.ts` 의 Observation·Action 설명과 같다). 그래서 토픽을 뺀 이름으로 묶는다.
 * 카드 안에서는 토픽마다 계열 하나를 만들어 한 차트에 겹친다.
 *
 * Owns:
 * - 카드의 식별자(토픽을 뺀 이름)와 카드 안 계열 목록·표시 이름.
 * - 카드 정렬(팔 Left → Right → 공통, 그 안에서 이름순)과 지표 표시 순서.
 * - 계열 표기(색·파선) 고정표.
 *
 * Does not own:
 * - 표본·y축 계산(`motionRendering.ts`), 적재(`useMotionSeries.ts`), 체크박스 상태(보기).
 */

/** 카드를 만드는 데 필요한 최소 계약. 개요 `series` 와 상세 색인이 모두 만족한다. */
export type MotionSeriesId = { channel: string; metric: string };

export type MotionCardSeries = {
  group: ParquetChannelGroupId;
  /** `<topic>::<name>` 원본 키. 표본을 찾는 키다. */
  channel: string;
  /** 최댓값 배지가 표시할 이름. */
  title: string;
  metrics: readonly string[];
};

export type MotionChannelCard = {
  /** 토픽을 뺀 채널 이름. React key 와 체크박스 id 접두사로도 쓴다. */
  id: string;
  title: string;
  side: ArmSide | null;
  /** `GROUP_ORDER` 순. 여기 있는 그룹마다 체크박스를 하나씩 만든다. */
  series: readonly MotionCardSeries[];
  /** 카드 안 지표 합집합. 표시 순서대로 둔다. */
  metrics: readonly string[];
};

/** 기본 탭이 그리는 지표. 상류가 지표를 늘려도 이 둘은 항상 앞에 둔다. */
export const MOTION_PRIMARY_METRICS: readonly string[] = ["speed", "acceleration"];

export function isPrimaryMotionMetric(metric: string): boolean {
  return MOTION_PRIMARY_METRICS.includes(metric);
}

/** 추가 지표 탭이 그리는 지표. 속력 증감률과 성분별 토크다. */
export function isExtraMotionMetric(metric: string): boolean {
  return metric === "speed_change_rate" || metric.startsWith("torque.");
}

/**
 * 계열 표기. **모든 카드에 공통으로 고정**한다.
 *
 * 채널로 이미 차트를 나눴다. 그래서 색은 한 차트 안에서 명령 값과 측정 값을 구분하는 데만 쓴다.
 * 카드마다 색이 다르면 사용자가 카드마다 범례를 다시 읽어야 한다.
 */
export const MOTION_SERIES_STYLE: Record<
  ParquetChannelGroupId,
  { color: string; dash: readonly number[] }
> = {
  observation: { color: CHART_SERIES_PALETTE[0], dash: [] },
  action: { color: CHART_SERIES_PALETTE[1], dash: [6, 3] },
  other: { color: CHART_SERIES_PALETTE[2], dash: [2, 2] },
};

/** 진단 그리드 키(`<topic>::<name>`)의 name 부분. 구분자가 없으면 키 전체. */
function keyName(key: string): string {
  const index = key.indexOf("::");
  return index === -1 ? key : key.slice(index + 2);
}

const SIDE_RANK: Record<"left" | "right" | "common", number> = { left: 0, right: 1, common: 2 };

/** 카드 안 지표 순서. 속력·가속도가 먼저이고, 나머지는 이름순이다. */
function orderMetrics(metrics: Iterable<string>): string[] {
  return [...new Set(metrics)].sort((a, b) => {
    const rank = (metric: string) => {
      const index = MOTION_PRIMARY_METRICS.indexOf(metric);
      return index === -1 ? MOTION_PRIMARY_METRICS.length : index;
    };
    return rank(a) - rank(b) || a.localeCompare(b);
  });
}

/**
 * 계열 목록을 채널 카드로 묶는다.
 *
 * 개요와 상세는 **같은 카드 목록을 만들어야 한다**. 상세가 도착할 때 목록이 바뀌면 카드가
 * 리마운트된다. 그러면 체크박스와 y축 토글 상태가 사라진다. 그래서 입력은 `{channel, metric}`
 * 만 받는다.
 */
export function motionChannelCards(
  series: readonly MotionSeriesId[],
  channels: { get(key: string): DatasetStructureChannel | undefined },
): readonly MotionChannelCard[] {
  const cards = new Map<string, Map<string, Set<string>>>();
  for (const item of series) {
    const id = keyName(item.channel);
    const byChannel = cards.get(id) ?? new Map<string, Set<string>>();
    cards.set(id, byChannel);
    const metrics = byChannel.get(item.channel) ?? new Set<string>();
    byChannel.set(item.channel, metrics);
    metrics.add(item.metric);
  }
  return [...cards.entries()]
    .map(([id, byChannel]) => {
      const sources = [...byChannel.entries()]
        .map(([channel, metrics]) => ({
          group: renderGroup(channels.get(channel), channel),
          channel,
          title: channelTitle(channel, channels.get(channel)),
          metrics: orderMetrics(metrics),
        }))
        .sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));
      // 한 계열이라도 방향을 알려 주면 그 방향을 쓴다. 이름이 같은 채널들이라 판정이 서로
      // 다를 일은 없다. 그러나 한쪽만 구조에 있는 경우에도 그쪽의 `arm_side` 를 쓸 수 있다.
      const side =
        sources
          .map((source) => displayArmSide(channels.get(source.channel), source.channel))
          .find((value) => value !== null) ?? null;
      // 구조를 모르면 `channelTitle` 은 토픽이 붙은 원본 키를 돌려준다. 카드는 여러 토픽을 묶은
      // 단위다. 그중 한 키를 제목으로 쓰면 다른 토픽의 계열이 다른 카드의 것처럼 보인다. 그래서
      // 토픽을 뺀 이름을 제목으로 쓴다.
      const known = sources.find((source) => channels.get(source.channel) !== undefined);
      return {
        id,
        title: known === undefined ? id : known.title,
        side,
        series: sources,
        metrics: orderMetrics(sources.flatMap((source) => source.metrics)),
      };
    })
    .sort(
      (a, b) =>
        SIDE_RANK[a.side ?? "common"] - SIDE_RANK[b.side ?? "common"] || a.id.localeCompare(b.id),
    );
}

/** 이 탭이 보는 지표만 남긴다. 남는 지표가 없는 카드는 뺀다. 빈 카드는 채널이 없다고 잘못 알린다. */
export function motionCardsWithMetrics(
  cards: readonly MotionChannelCard[],
  keep: (metric: string) => boolean,
): readonly MotionChannelCard[] {
  return cards.flatMap((card) => {
    const metrics = card.metrics.filter(keep);
    if (metrics.length === 0) return [];
    const series = card.series.flatMap((source) => {
      const kept = source.metrics.filter(keep);
      return kept.length === 0 ? [] : [{ ...source, metrics: kept }];
    });
    return [{ ...card, metrics, series }];
  });
}

/**
 * 좌우로 짝지은 행, 또는 전폭 한 장.
 *
 * 좌우를 세로로 떨어뜨리면 같은 채널의 두 팔을 한 번에 비교할 수 없다. 그래서 한 행에 나란히
 * 둔다. 짝이 없는 쪽은 **빈 칸으로 남긴다**. 칸을 접으면 아래 행이 한 칸씩 이동해 좌우 열이
 * 어긋난다.
 */
export type MotionCardRow =
  | { kind: "arms"; key: string; left: MotionChannelCard | null; right: MotionChannelCard | null }
  | { kind: "common"; key: string; card: MotionChannelCard };

/** 팔 방향 토큰을 뺀 이름. `ee_pose_left` 와 `ee_pose_right` 를 한 행으로 묶는 키다. */
function armBaseName(id: string): string {
  const tokens = id.split(/[_-]/);
  const kept = tokens.filter((token) => !DIRECTION_TOKEN.test(token));
  return kept.length === tokens.length ? id : kept.join("_");
}

/** 양쪽 팔에 카드가 하나 이상씩 있는지 여부. 이 값으로 좌우 열을 만들지 정한다. */
export function hasBothArms(cards: readonly MotionChannelCard[]): boolean {
  return cards.some((card) => card.side === "left") && cards.some((card) => card.side === "right");
}

/**
 * 카드를 화면 행으로 묶는다. 좌우 카드는 방향 토큰을 뺀 이름으로 짝짓는다. 팔이 없는 카드는
 * 혼자 한 행을 차지한다. 행 순서는 카드 순서를 그대로 따른다.
 */
export function motionCardRows(cards: readonly MotionChannelCard[]): readonly MotionCardRow[] {
  const rows: MotionCardRow[] = [];
  const armRows = new Map<
    string,
    { kind: "arms"; key: string; left: MotionChannelCard | null; right: MotionChannelCard | null }
  >();
  for (const card of cards) {
    if (card.side === null) {
      rows.push({ kind: "common", key: card.id, card });
      continue;
    }
    const key = armBaseName(card.id);
    let row = armRows.get(key);
    if (row === undefined) {
      row = { kind: "arms", key, left: null, right: null };
      armRows.set(key, row);
      rows.push(row);
    }
    row[card.side] = card;
  }
  return rows;
}
